import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  useTheme,
  Avatar,
  Button,
  Badge,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { LightTooltip } from "../../styled/CustomComponent";
import AddIcon from "@mui/icons-material/Add";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PlayCircleFilledWhiteOutlinedIcon from "@mui/icons-material/PlayCircleFilledWhiteOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import BoundaryIcon from "@mui/icons-material/BorderAll";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { font } from "../../../styles/font";
import { CARD_COLOR } from "../../../constant/ApplicationJson";
import { COLORS } from "../../../styles/theme";
import { TestCaseCustomIcon } from "../../styled/CustomIcons";
import CreatedByAvatar from "../../common/CreatedByAvatar";
import WebhookOutlinedIcon from "@mui/icons-material/WebhookOutlined";
import { useState, useMemo } from "react";

const formatDate = (dateString) => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return null;
  }
};

const RegressionSuiteCard = ({
  suite,
  onClick,
  onExecute,
  onDelete,
  onQuickAdd,
  onAddMore,
  onEdit,
  handleNewSuiteClick,
  viewMode = "grid",
}) => {
  const theme = useTheme();
  const cardColor = CARD_COLOR["regression"] || theme.palette.primary.main;

  // Calculate boundary enabled test cases - FIXED
  const { hasBoundaryEnabled, boundaryCount } = useMemo(() => {
    const testCases = suite.testCases || [];
    
    const enabledTestCases = testCases.filter(tc => {
      if (tc.boundaryEnabled === true) return true;
      if (tc.boundaryFieldCategorySelections && tc.boundaryFieldCategorySelections.length > 0) return true;
      if (tc.boundaryConfig && tc.boundaryConfig.length > 0) return true;
      if (tc.boundaryFieldCategorySelections && Object.keys(tc.boundaryFieldCategorySelections).length > 0) return true;
      return false;
    });
    
    return {
      hasBoundaryEnabled: enabledTestCases.length > 0,
      boundaryCount: enabledTestCases.length
    };
  }, [suite.testCases]);

  const totalTestCases = suite.totalTests || suite.testCases?.length || 0;
  const hasRelease = !!suite.releaseName;
  const hasDescription = !!suite.description?.trim();
  const createdDate = suite.createdAt || suite.createdDate || suite.created_on;
  const formattedDate = formatDate(createdDate);

  // List View Component
  if (viewMode === "list") {
    return (
      <Paper
        elevation={0}
        onClick={() => onClick(suite)}
        sx={{
          mb: 2,
          p: 2,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          transition: "all 0.2s",
          "&:hover": {
            backgroundColor: theme.palette.highlight.main,
            transform: "translateY(-2px)",
            boxShadow: theme.shadows[2],
          },
          backgroundColor: theme.palette.card.main,
          ...(hasBoundaryEnabled && {
            borderLeft: `4px solid ${theme.palette.secondary.main}`,
          }),
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 1,
              backgroundColor: theme.palette.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <WebhookOutlinedIcon sx={{ color: "white" }} />
            {hasBoundaryEnabled && (
              <Badge
                badgeContent={boundaryCount}
                color="secondary"
                sx={{
                  position: "absolute",
                  top: -8,
                  right: -8,
                  "& .MuiBadge-badge": {
                    fontSize: 10,
                    height: 18,
                    minWidth: 18,
                    borderRadius: 9,
                  },
                }}
              />
            )}
          </Box>
          
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {suite.name}
              </Typography>
              
              {hasRelease ? (
                <Chip
                  size="small"
                  label={suite.releaseName}
                  sx={{
                    fontSize: font.size.fs_6,
                    color: theme.palette.secondary,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  }}
                />
              ) : (
                <Tooltip title="No release assigned">
                  <Chip
                    size="small"
                    label="No Release"
                    variant="outlined"
                    icon={<ErrorOutlineOutlinedIcon sx={{ fontSize: 12 }} />}
                    sx={{
                      fontSize: font.size.fs_6,
                      color: theme.palette.text.secondary,
                      borderColor: theme.palette.divider,
                      backgroundColor: alpha(theme.palette.text.disabled, 0.05),
                    }}
                  />
                </Tooltip>
              )}
              
              {hasBoundaryEnabled && (
                <Chip
                  size="small"
                  icon={<BoundaryIcon sx={{ fontSize: 14 }} />}
                  label={`Boundary: ${boundaryCount}`}
                  color="secondary"
                  variant="outlined"
                  sx={{
                    fontSize: font.size.fs_6,
                    height: 24,
                  }}
                />
              )}
            </Box>
            
            {hasDescription ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {suite.description}
              </Typography>
            ) : (
              <Typography 
                variant="body2" 
                color="text.disabled" 
                sx={{ 
                  mb: 1, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  fontStyle: 'italic'
                }}
              >
                <DescriptionOutlinedIcon sx={{ fontSize: 14 }} />
                No description added
              </Typography>
            )}
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                icon={<TestCaseCustomIcon sx={{ fontSize: 14 }} />}
                size="small"
                label={`${totalTestCases} ${totalTestCases === 1 ? 'Test Case' : 'Test Cases'}`}
                variant="outlined"
                sx={{
                  fontSize: font.size.fs_6,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.4)}`,
                  color: theme.palette.secondary,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                }}
              />
              
              {formattedDate && (
                <Typography 
                  variant="caption" 
                  color="text.disabled"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <CalendarTodayOutlinedIcon sx={{ fontSize: 12 }} />
                  Created {formattedDate}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ display: "flex", gap: 1 }}>
          <LightTooltip title="Execute Regression Plan">
            <IconButton 
              size="small" 
              onClick={(e) => { e.stopPropagation(); onExecute(suite); }}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <PlayCircleFilledWhiteOutlinedIcon />
            </IconButton>
          </LightTooltip>
          <LightTooltip title="Edit Regression Plan">
            <IconButton 
              size="small" 
              onClick={(e) => { e.stopPropagation(); onEdit(suite); }}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <EditOutlinedIcon />
            </IconButton>
          </LightTooltip>
          <LightTooltip title="Delete Regression Plan">
            <IconButton 
              size="small" 
              color="error" 
              onClick={(e) => { e.stopPropagation(); onDelete(suite); }}
            >
              <DeleteIcon />
            </IconButton>
          </LightTooltip>
        </Box>
      </Paper>
    );
  }

  // Grid View Component (Card)
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: 0,
        paddingTop: "75%", // Changed to 75% for better card proportions
      }}
    >
      <Paper
        elevation={0}
        onClick={() => onClick(suite)}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          p: 2.5,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          overflow: "hidden",
          transition: "all 0.2s",
          "&:hover": {
            backgroundColor: theme.palette.highlight.main,
            transform: "translateY(-4px)",
            boxShadow: theme.shadows[4],
          },
          backgroundColor: theme.palette.card.main,
          ...(hasBoundaryEnabled && {
            borderLeft: `4px solid ${theme.palette.secondary.main}`,
          }),
        }}
      >
        {/* Top Row: Icon + Action Buttons */}
        <Box className="flex justify-between items-start">
          <Box
            sx={{
              width: 40,
              height: 40,
              minWidth: 40,
              minHeight: 40,
              borderRadius: 1.5,
              backgroundColor: theme.palette.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "default",
              position: "relative",
            }}
          >
            <WebhookOutlinedIcon
              sx={{
                color: "white",
                fontSize: font.size.fs_3,
              }}
            />
            {hasBoundaryEnabled && (
              <Badge
                badgeContent={boundaryCount}
                color="secondary"
                sx={{
                  position: "absolute",
                  top: -8,
                  right: -8,
                  "& .MuiBadge-badge": {
                    fontSize: 10,
                    height: 18,
                    minWidth: 18,
                    borderRadius: 9,
                  },
                }}
              />
            )}
          </Box>

          <Box className="flex gap-1">
            <LightTooltip title="Execute Regression Plan">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onExecute(suite);
                }}
                sx={{
                  backgroundColor: "transparent",
                  color: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <PlayCircleFilledWhiteOutlinedIcon />
              </IconButton>
            </LightTooltip>
            <LightTooltip title="Edit Regression Plan">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(suite);
                }}
                sx={{
                  backgroundColor: "transparent",
                  color: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <EditOutlinedIcon />
              </IconButton>
            </LightTooltip>
            <LightTooltip title="Delete Regression Plan">
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(suite);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </LightTooltip>
          </Box>
        </Box>

        {/* Name and Release Section */}
        <Box sx={{ overflow: "hidden", minHeight: 0 }}>
          <Tooltip title={suite.name}>
            <Typography 
              fontWeight={600} 
              fontSize={font.size.fs_4} 
              noWrap
              sx={{ mb: 1 }}
            >
              {suite.name}
            </Typography>
          </Tooltip>
          
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            {hasRelease ? (
              <Chip
                size="small"
                label={suite.releaseName}
                sx={{
                  fontSize: font.size.fs_6,
                  color: theme.palette.secondary,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                  },
                }}
              />
            ) : (
              <Tooltip title="No release assigned - you can add one while editing">
                <Chip
                  size="small"
                  label="No Release"
                  variant="outlined"
                  icon={<ErrorOutlineOutlinedIcon sx={{ fontSize: 12 }} />}
                  sx={{
                    fontSize: font.size.fs_6,
                    color: theme.palette.text.secondary,
                    borderColor: theme.palette.divider,
                    backgroundColor: alpha(theme.palette.text.disabled, 0.05),
                  }}
                />
              </Tooltip>
            )}
            
            {hasBoundaryEnabled && (
              <Chip
                icon={<BoundaryIcon sx={{ fontSize: 14 }} />}
                size="small"
                label={`Boundary: ${boundaryCount}`}
                color="secondary"
                variant="outlined"
                sx={{
                  fontSize: font.size.fs_6,
                  height: 24,
                  width: 'fit-content',
                  '& .MuiChip-icon': {
                    fontSize: 14,
                  },
                }}
              />
            )}
          </Box>
          
          {/* Description Section */}
          {hasDescription ? (
            <Typography
              fontSize={font.size.fs_5}
              color={theme.palette.text.secondary}
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                mb: 1.5,
                lineHeight: 1.4,
              }}
            >
              {suite.description}
            </Typography>
          ) : (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.75,
                mb: 1.5,
                p: 1,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.text.disabled, 0.05),
                border: `1px dashed ${theme.palette.divider}`,
              }}
            >
              <DescriptionOutlinedIcon sx={{ fontSize: 16, color: theme.palette.text.disabled }} />
              <Typography
                fontSize={font.size.fs_6}
                color="text.disabled"
                fontStyle="italic"
              >
                No description added. Click edit to add one.
              </Typography>
            </Box>
          )}

          {/* Test case chip and metadata */}
          <Box sx={{ flexShrink: 0, display: "flex", gap: 1, flexWrap: "wrap", alignItems: 'center', justifyContent: 'space-between' }}>
            <Chip
              icon={<TestCaseCustomIcon sx={{ fontSize: 14, color: theme.palette.secondary.main }} />}
              size="small"
              label={`${totalTestCases} ${totalTestCases === 1 ? 'Test Case' : 'Test Cases'}`}
              sx={{
                height: 28,
                px: 1,
                fontSize: font.size.fs_6,
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.4)}`,
                color: theme.palette.secondary,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.15),
                },
              }}
            />
            
            {formattedDate && (
              <Tooltip title="Creation date">
                <Typography 
                  variant="caption" 
                  color="text.disabled"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <CalendarTodayOutlinedIcon sx={{ fontSize: 12 }} />
                  {formattedDate}
                </Typography>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Created By Footer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 0.75,
            flexShrink: 0,
            mt: 'auto',
            pt: 1,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          }}
        >
          <Avatar
            sx={{
              width: 24,
              height: 24,
              fontSize: font.size.fs_6,
              bgcolor: alpha(theme.palette.info.main, 0.15),
              color: theme.palette.info.main,
            }}
          >
            {suite.createdBy?.[0]?.toUpperCase() || "U"}
          </Avatar>
          <Tooltip title={`Created by ${suite.createdBy || "Unknown user"}`}>
            <Typography
              fontSize={font.size.fs_6}
              color={theme.palette.info.main}
              noWrap
              sx={{ maxWidth: 120 }}
            >
              {suite.createdBy || "Unknown User"}
            </Typography>
          </Tooltip>
        </Box>
      </Paper>
    </Box>
  );
};

export default RegressionSuiteCard;